import { get, post, put, del } from '@/services/http';
import { ParamList } from '@/collections/params';

export interface IBuyingListItem {
  id?: number;
  buyingListId?: number;
  variantId: number;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  _action?: 'add' | 'update' | 'remove';
}

export interface IBuyingList {
  id?: number;
  name: string;
  description?: string;
  status: 'draft' | 'finalized' | 'completed' | 'canceled';
  supplierId?: number;
  locationId?: number;
  totalValue: number;
  items: IBuyingListItem[];
  updatedAt?: string;
  createdAt?: string;
}

interface ISendEmailOptions {
  emailSubject: string;
  customMessage: string;
  attachmentFormat: 'pdf' | 'csv';
  recipients: string[];
}

export interface IProduct {
  id: number;
  name: string;
  currentStock: number;
  reorderPoint: number;
  reorderQuantity: number;
  leadTime: number;
  buyingCycle: number;
  nextReorderDate: string;
  unitPrice: number;
  category: string;
}

export interface ISupplier {
  id: number;
  name: string;
  buyingCycle: number;
  products: IProduct[];
}

export interface ISupplierGroup {
  supplierId: number;
  supplierName: string;
  supplierBuyingCycle: number;
  products: IProduct[];
}

export type IViewMode = 'day' | 'week' | 'month';

export interface DateRange {
  from: Date;
  to: Date;
}

export interface Product {
  variant_image: string;
  'Item[Item Description]': string;
  variant?: string;
  variant_id?: string;
  product_sku: string;
  product_type: string;
  '[Actual Inventory Quantity]': number;
  '[reorder_point]': number;
  '[vendor_lead_time]': number;
  '[average_daily_units_sold_param]': number;
  '[inventory_replenishment]': number;
  unit_cost: number;
  '[reorder_date]': string;
  // Add other fields as needed
}

export interface Supplier {
  id: string;
  name: string;
  buyingCycle: number;
  products: Product[];
}

export interface Period {
  period: string;
  suppliers: Supplier[];
}

export interface BuyingCalendarResponse {
  success: boolean;
  data: {
    periods: Period[];
  };
  dimension: string;
  meta: {
    total: number;
    page: number;
    per_page: number;
  };
}

export class BuyingCalendarModel {
  public async fetchBuyingCalendar(
    viewMode: string,
    dateRange: DateRange,
    locationIds: number[] | string,
    reportParams?: ParamList,
    historicalDateRange?: string[],
    productLimit?: number,
    dimension?: string,
  ): Promise<any> {
    try {
      let url = `/buying_calendar?from=${dateRange.from.toISOString().split('T')[0]}&to=${dateRange.to.toISOString().split('T')[0]}&period=${viewMode}`;

      if (Array.isArray(locationIds) && locationIds.length > 0) {
        url += `&location_ids=${locationIds.join(',')}`;
      }
      // In all other cases (empty array, any string), the location_ids parameter will be omitted.
      // The backend will interpret the absence of this parameter as a request for all locations.

      // Add product limit parameter
      if (productLimit) {
        url += `&max_products_per_supplier=${productLimit}`;
      }

      // Add historical date range parameters if provided
      if (historicalDateRange && historicalDateRange.length >= 2) {
        url += `&historical_from=${encodeURIComponent(historicalDateRange[0])}`;
        url += `&historical_to=${encodeURIComponent(historicalDateRange[1])}`;
      }

      // Add report parameters if provided
      if (reportParams && reportParams.size() > 0) {
        for (const param of reportParams.items) {
          if (param.value && param.value.length > 0) {
            // Handle array values (join with comma)
            const paramValue = Array.isArray(param.value)
              ? param.value.join(',')
              : param.value;
            url += `&${param.code}=${encodeURIComponent(paramValue)}`;
          }
        }
      }

      if (dimension) {
        url += `&dimension=${encodeURIComponent(dimension)}`;
      }

      const response: any = await get(url);
      return {
        periods: response.data.periods,
        dimension: response.dimension || 'by_variant',
      };
    } catch (error) {
      console.error('Error fetching buying calendar:', error);
      return { periods: [], dimension: 'by_variant' };
    }
  }
}
